import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HelpConnectCourseDaysCard = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'system_admin';
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/helpconnect-course-days`).then((r) => setData(r.data))
      .catch((e) => { setData({ default_days: 30, courses: [] }); toast.error(e.response?.data?.detail || 'Failed to load HELPConnect courses'); });
  }, []);

  const setCourse = (id, v) => setData((d) => ({ ...d, courses: d.courses.map((c) => (c.program_id === id ? { ...c, days: v } : c)) }));
  const save = async () => {
    setSaving(true);
    try {
      const course_days = Object.fromEntries(data.courses.map((c) => [c.program_id, c.days === '' || c.days == null ? null : Number(c.days)]));
      const r = await axios.put(`${API}/settings/helpconnect-course-days`, { default_days: Number(data.default_days), course_days });
      setData(r.data);
      toast.success('Course deadlines saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save course deadlines');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-shadow" data-testid="helpconnect-course-days-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5" />HELPConnect Course Deadlines</CardTitle>
        <CardDescription>
          Each course is due this many days after the participant enrolls. The Learning Calendar spreads the remaining lessons evenly up to that deadline. Leave a course blank to use the default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <>
            <label className="flex items-center gap-3 text-sm font-medium">
              Default days
              <Input type="number" min={1} max={730} className="w-24" value={data.default_days} disabled={!canEdit}
                onChange={(e) => setData({ ...data, default_days: e.target.value })} data-testid="helpconnect-default-days-input" />
            </label>
            <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
              {data.courses.map((c) => (
                <div key={c.program_id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`helpconnect-course-days-row-${c.program_id}`}>
                  <span className="text-sm truncate">{c.title}</span>
                  <Input type="number" min={1} max={730} className="w-24 shrink-0" placeholder={String(data.default_days)} value={c.days ?? ''} disabled={!canEdit}
                    onChange={(e) => setCourse(c.program_id, e.target.value)} data-testid={`helpconnect-course-days-input-${c.program_id}`} />
                </div>
              ))}
            </div>
            {canEdit && (
              <Button onClick={save} disabled={saving} data-testid="helpconnect-course-days-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Deadlines
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
